import {
    AibackReportAspectRowEntity,
    AibackReportPdfTopItemEntity,
    AibackReportPdfTreemapItemEntity,
} from '@/entities/aiback-report.entity';
import { BrandGroupEntity } from '@/entities/brand-group.entity';
import { ProductGroupEntity } from '@/entities/product-group.entity';
import {
    buildRatingBucketsFromReviewRows,
    buildTopItemsFromRows,
    buildTreemapItemsFromRows,
} from '@/domain/reporting/reporting-shared.collections';

export type ReportModalProductRow = {
    id: string;
    name: string;
    reviewCount: number;
    answerCount: number;
    aiAnalysisCount: number;
    averageRating: number | null;
};

export type ReportModalBrandRow = {
    id: string;
    name: string;
    reviewCount: number;
    averageRating: number | null;
};

export type ReportModalAspectRadarPoint = {
    key: string;
    title: string;
    positiveAverageIntensity: number;
    negativeAverageIntensity: number;
};

type ReportModalTopItemSource = {
    name: string;
    reviewCount: number;
    averageRating: number | null;
};

export const prepareReportModalProducts = (products: ProductGroupEntity[]): ReportModalProductRow[] =>
    products.map((product) => ({
        id: product.id,
        name: product.name,
        reviewCount: Math.max(0, product.reviewCount ?? 0),
        answerCount: Math.max(0, product.answerCount ?? 0),
        aiAnalysisCount: Math.max(0, product.aiAnalysisCount ?? 0),
        averageRating: typeof product.averageRating === 'number' ? product.averageRating : null,
    }));

export const prepareReportModalBrands = (brands: BrandGroupEntity[]): ReportModalBrandRow[] =>
    brands.map((brand) => ({
        id: brand.id,
        name: brand.name,
        reviewCount: Math.max(0, brand.reviewCount ?? 0),
        averageRating: typeof brand.averageRating === 'number' ? brand.averageRating : null,
    }));

export const sumReportModalMetric = <T>(rows: T[], getter: (row: T) => number): number =>
    rows.reduce((acc, row) => acc + Math.max(0, getter(row) || 0), 0);

export const calculateReportModalWeightedAverageRating = (
    rows: Array<{
        reviewCount: number;
        averageRating: number | null;
    }>,
): number | null => {
    const totals = rows.reduce(
        (acc, row) => {
            if (typeof row.averageRating !== 'number' || row.reviewCount <= 0) {
                return acc;
            }

            return {
                weightedRatingSum: acc.weightedRatingSum + row.averageRating * row.reviewCount,
                weightedReviewsCount: acc.weightedReviewsCount + row.reviewCount,
            };
        },
        {
            weightedRatingSum: 0,
            weightedReviewsCount: 0,
        },
    );

    if (totals.weightedReviewsCount > 0) {
        return totals.weightedRatingSum / totals.weightedReviewsCount;
    }

    const ratings = rows
        .map((row) => row.averageRating)
        .filter((currentRating): currentRating is number => typeof currentRating === 'number');

    if (!ratings.length) {
        return null;
    }

    const ratingSum = ratings.reduce((acc, rating) => acc + rating, 0);
    return ratingSum / ratings.length;
};

export const buildReportModalTopItems = <T extends ReportModalTopItemSource>(
    rows: T[],
    limit = 3,
): AibackReportPdfTopItemEntity[] => buildTopItemsFromRows(rows, { limit });

export const buildReportModalRatingBuckets = (
    products: ReportModalProductRow[],
): Array<{ stars: number; value: number }> => buildRatingBucketsFromReviewRows(products);

export const buildReportModalTreemapItems = (products: ReportModalProductRow[]): AibackReportPdfTreemapItemEntity[] =>
    buildTreemapItemsFromRows(products);

export const buildReportModalAspectRadarPoints = (
    rows: AibackReportAspectRowEntity[],
    maxItems = 10,
): ReportModalAspectRadarPoint[] =>
    rows
        .filter((row) => row.positiveCount > 0 || row.negativeCount > 0)
        .slice(0, Math.max(0, Math.floor(maxItems)))
        .map((row) => ({
            key: row.key,
            title: row.title,
            positiveAverageIntensity: row.positiveAverageIntensity,
            negativeAverageIntensity: row.negativeAverageIntensity,
        }));
